import React, { useEffect, useState } from 'react';
import { C, F, T, Btn, Pill, Card, Shell, PageTitle } from '../theme/uiKit';
import { getSupplierInsightsService } from '../services';
import type {
    InsightsOverview,
    InsightsSupplier,
    InsightsTransaction,
    InsightsPurchaseOrder,
    InsightsPoLine,
    InsightsOutcome,
} from '../services/types';

// Supplier Insights — internal reporting on supplier performance. Ported from
// the dscs prototypes (Overview + ASN Review + Supplier Review + PO Review +
// PO Lines Review), wired to the SupplierInsightsService. Data is empty/zeroed
// until the backend is built.

type InsightsView = 'dashboard' | 'transactions' | 'suppliers' | 'pos' | 'polines';

const INSIGHTS_NAV = [
    { id: 'dashboard', label: 'Overview' },
    { id: 'transactions', label: 'ASN Review' },
    { id: 'suppliers', label: 'Supplier Review' },
    { id: 'pos', label: 'Purchase Orders Review' },
    { id: 'polines', label: 'PO Lines Review' },
];

// ── Shared date-range control ─────────────────────────────────────────────────
type Preset = '7d' | '30d' | '90d';
const PRESETS: Preset[] = ['7d', '30d', '90d'];

function DateRange({ preset, onChange }: { preset: Preset; onChange: (p: Preset) => void }) {
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
            <span style={{ fontSize: T.body, color: C.textSub, fontWeight: 500 }}>Date range:</span>
            <div
                style={{
                    display: 'flex',
                    gap: 4,
                    background: C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 8,
                    padding: 3,
                }}
            >
                {PRESETS.map((p) => (
                    <button
                        key={p}
                        type="button"
                        onClick={() => onChange(p)}
                        style={{
                            padding: '6px 14px',
                            borderRadius: 6,
                            border: 'none',
                            cursor: 'pointer',
                            fontFamily: F.ui,
                            fontSize: T.body,
                            fontWeight: preset === p ? 700 : 500,
                            background: preset === p ? C.blue : 'transparent',
                            color: preset === p ? '#fff' : C.textSub,
                        }}
                    >
                        {p}
                    </button>
                ))}
            </div>
        </div>
    );
}

function EmptyRow({ span, message }: { span: number; message: string }) {
    return (
        <tr>
            <td colSpan={span} style={{ padding: 30, textAlign: 'center', color: C.textMuted }}>
                {message}
            </td>
        </tr>
    );
}

// ── Overview dashboard ────────────────────────────────────────────────────────
function OverviewView({ overview }: { overview: InsightsOverview }) {
    const [preset, setPreset] = useState<Preset>('30d');

    const kpis = [
        { label: 'Total ASNs', value: overview.asnCount ? overview.asnCount.toLocaleString() : '—', sub: 'shipments notified' },
        { label: 'Suppliers', value: overview.supplierCount || '—', sub: 'active this period' },
        { label: 'On-time %', value: overview.onTimePct ? `${overview.onTimePct}%` : '—', sub: 'against promised dates' },
        { label: 'Error rate', value: overview.errorRate ? `${overview.errorRate}%` : '—', sub: 'data + business errors' },
    ];

    return (
        <>
            <DateRange preset={preset} onChange={setPreset} />
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gap: 14,
                    marginBottom: 16,
                }}
            >
                {kpis.map((k) => (
                    <Card key={k.label}>
                        <div style={{ padding: '14px 16px' }}>
                            <div style={{ fontSize: T.caption, color: C.textMuted, marginBottom: 2 }}>
                                {k.label}
                            </div>
                            <div style={{ fontSize: T.display, fontWeight: 700, color: C.text }}>
                                {k.value}
                            </div>
                            <div style={{ fontSize: T.caption, color: C.textMuted, marginTop: 4 }}>
                                {k.sub}
                            </div>
                        </div>
                    </Card>
                ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
                <Card>
                    <div
                        style={{
                            padding: '12px 16px',
                            borderBottom: `1px solid ${C.borderLight}`,
                            fontSize: T.body,
                            fontWeight: 600,
                        }}
                    >
                        ASN volume trend
                    </div>
                    <div style={{ padding: 40, textAlign: 'center', color: C.textMuted }}>
                        No data to chart yet.
                    </div>
                </Card>
                <Card>
                    <div
                        style={{
                            padding: '12px 16px',
                            borderBottom: `1px solid ${C.borderLight}`,
                            fontSize: T.body,
                            fontWeight: 600,
                        }}
                    >
                        Supplier adoption
                    </div>
                    <div style={{ padding: 40, textAlign: 'center', color: C.textMuted }}>
                        No data to chart yet.
                    </div>
                </Card>
            </div>
        </>
    );
}

// ── CSV export helper ─────────────────────────────────────────────────────────
function downloadCsv(filename: string, rows: (string | number)[][]) {
    const esc = (v: string | number) => {
        const s = String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const content = rows.map((r) => r.map(esc).join(',')).join('\n');
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

const PAGE_SIZE = 15;

// Shared pagination footer.
function Pager({
    total,
    page,
    pageCount,
    onPrev,
    onNext,
}: {
    total: number;
    page: number;
    pageCount: number;
    onPrev: () => void;
    onNext: () => void;
}) {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '12px 16px',
                borderTop: `1px solid ${C.borderLight}`,
            }}
        >
            <span style={{ fontSize: T.caption, color: C.textMuted }}>
                Showing {total === 0 ? 0 : page * PAGE_SIZE + 1}–
                {Math.min((page + 1) * PAGE_SIZE, total)} of {total}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Btn label="← Prev" variant="sec" size="xs" onClick={onPrev} />
                <span style={{ fontSize: T.caption, color: C.textSub }}>
                    Page {page + 1} of {pageCount}
                </span>
                <Btn label="Next →" variant="sec" size="xs" onClick={onNext} />
            </div>
        </div>
    );
}

// ── ASN Review (all transactions) ─────────────────────────────────────────────
type Bucket = 'passed' | 'data' | 'business';
const BUCKET_FILTERS: { id: Bucket; label: string }[] = [
    { id: 'passed', label: 'Passed' },
    { id: 'data', label: 'Supplier data errors' },
    { id: 'business', label: 'Business errors' },
];
function bucketOf(o: InsightsOutcome): Bucket {
    if (o === 'passed') return 'passed';
    if (o === 'business') return 'business';
    return 'data';
}
const OUTCOME_LABEL: Record<InsightsOutcome, string> = {
    passed: 'Passed',
    schema: 'Supplier data error',
    semantic: 'Supplier data error',
    business: 'Business error',
};
const OUTCOME_COLOR: Record<InsightsOutcome, 'green' | 'amber' | 'red'> = {
    passed: 'green',
    schema: 'amber',
    semantic: 'amber',
    business: 'red',
};

function TransactionsView({ rows }: { rows: InsightsTransaction[] }) {
    const [preset, setPreset] = useState<Preset>('30d');
    const [selected, setSelected] = useState<Set<Bucket>>(new Set());
    const [q, setQ] = useState('');
    const [page, setPage] = useState(0);

    const toggle = (b: Bucket) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(b)) next.delete(b);
            else next.add(b);
            return next;
        });
        setPage(0);
    };

    const query = q.trim().toLowerCase();
    const filtered = rows.filter((t) => {
        if (selected.size > 0 && !selected.has(bucketOf(t.outcome))) return false;
        if (
            query &&
            !t.asn.toLowerCase().includes(query) &&
            !t.supplier.toLowerCase().includes(query) &&
            !t.pos.some((po) => po.toLowerCase().includes(query))
        )
            return false;
        return true;
    });
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const pageRows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

    const exportCsv = () =>
        downloadCsv('all-transactions.csv', [
            ['ASN', 'Supplier', 'POs', 'Lines', 'Received', 'Outcome', 'Error'],
            ...filtered.map((t) => [
                t.asn,
                t.supplier,
                t.pos.join(' | '),
                t.lines,
                t.date,
                OUTCOME_LABEL[t.outcome],
                t.error,
            ]),
        ]);

    const headers = ['ASN', 'Supplier', 'POs', 'Received', 'Outcome', 'Detail', 'Attempts'];

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <Btn label="⤓ Export" variant="sec" size="xs" onClick={exportCsv} />
            </div>
            <DateRange preset={preset} onChange={setPreset} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                <span style={{ fontSize: T.body, color: C.textSub, fontWeight: 500 }}>Outcome:</span>
                {BUCKET_FILTERS.map((o) => {
                    const on = selected.has(o.id);
                    return (
                        <button
                            key={o.id}
                            type="button"
                            onClick={() => toggle(o.id)}
                            style={{
                                padding: '5px 12px',
                                borderRadius: 20,
                                fontSize: T.caption,
                                fontWeight: 500,
                                cursor: 'pointer',
                                fontFamily: F.ui,
                                background: on ? C.blue : C.surface,
                                color: on ? '#fff' : C.textSub,
                                border: `1px solid ${on ? C.blue : C.border}`,
                            }}
                        >
                            {o.label}
                        </button>
                    );
                })}
                <div style={{ flex: 1 }} />
                <input
                    value={q}
                    onChange={(e) => {
                        setQ(e.target.value);
                        setPage(0);
                    }}
                    placeholder="Search ASN, PO, supplier…"
                    style={{
                        width: 260,
                        maxWidth: '100%',
                        height: 34,
                        border: `1px solid ${C.border}`,
                        borderRadius: 6,
                        padding: '0 12px',
                        fontSize: T.body,
                        fontFamily: F.ui,
                        boxSizing: 'border-box',
                    }}
                />
            </div>
            <Card>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.body }}>
                    <thead>
                        <tr style={{ background: C.bg, textAlign: 'left', color: C.textSub }}>
                            {headers.map((h) => (
                                <th key={h} style={{ padding: '9px 16px', fontWeight: 600 }}>
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.length === 0 ? (
                            <EmptyRow span={headers.length} message="No ASN transactions to show yet." />
                        ) : (
                            pageRows.map((t) => (
                                <tr key={t.asn} style={{ borderTop: `1px solid ${C.borderLight}` }}>
                                    <td style={{ padding: '9px 16px', fontFamily: F.mono, color: C.primary }}>
                                        {t.asn}
                                    </td>
                                    <td style={{ padding: '9px 16px' }}>{t.supplier}</td>
                                    <td style={{ padding: '9px 16px', fontFamily: F.mono, color: C.textSub }}>
                                        {t.pos[0]}
                                        {t.pos.length > 1 ? (
                                            <span style={{ marginLeft: 6, fontSize: T.caption, color: C.primary }}>
                                                +{t.pos.length - 1}
                                            </span>
                                        ) : null}
                                    </td>
                                    <td style={{ padding: '9px 16px', color: C.textMuted }}>{t.date}</td>
                                    <td style={{ padding: '9px 16px' }}>
                                        <Pill label={OUTCOME_LABEL[t.outcome]} color={OUTCOME_COLOR[t.outcome]} />
                                    </td>
                                    <td style={{ padding: '9px 16px' }}>
                                        {t.error ? (
                                            <span style={{ fontSize: T.caption, fontFamily: F.mono, color: C.textSub }}>
                                                {t.error}
                                            </span>
                                        ) : (
                                            <span style={{ color: C.textMuted }}>—</span>
                                        )}
                                    </td>
                                    <td style={{ padding: '9px 16px', color: t.attempts ? C.text : C.textMuted }}>
                                        {t.attempts || '—'}
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                <Pager
                    total={filtered.length}
                    page={safePage}
                    pageCount={pageCount}
                    onPrev={() => setPage((p) => Math.max(0, p - 1))}
                    onNext={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                />
            </Card>
        </>
    );
}

// ── Supplier Review (directory) ───────────────────────────────────────────────
function SuppliersView({ rows }: { rows: InsightsSupplier[] }) {
    const [q, setQ] = useState('');
    const [page, setPage] = useState(0);
    const query = q.trim().toLowerCase();
    const filtered = rows.filter(
        (s) =>
            query === '' ||
            s.name.toLowerCase().includes(query) ||
            s.id.toLowerCase().includes(query),
    );
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const pageRows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
    const headers = ['Supplier', 'ASNs', 'Compliance', 'Validation %', 'Downstream %', 'Last ASN', 'Status'];

    const complianceColor = (v: number) => {
        if (!v) return C.textMuted;
        if (v >= 95) return C.green;
        if (v >= 93) return C.amber;
        return C.red;
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
                <input
                    value={q}
                    onChange={(e) => {
                        setQ(e.target.value);
                        setPage(0);
                    }}
                    placeholder="Search supplier or vendor ID…"
                    style={{
                        width: 280,
                        maxWidth: '100%',
                        height: 38,
                        border: `1px solid ${C.border}`,
                        borderRadius: 6,
                        padding: '0 12px',
                        fontSize: T.body,
                        fontFamily: F.ui,
                        boxSizing: 'border-box',
                    }}
                />
            </div>
            <Card>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.body }}>
                    <thead>
                        <tr style={{ background: C.bg, textAlign: 'left', color: C.textSub }}>
                            {headers.map((h) => (
                                <th key={h} style={{ padding: '9px 16px', fontWeight: 600 }}>
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.length === 0 ? (
                            <EmptyRow span={headers.length} message="No suppliers to show yet." />
                        ) : (
                            pageRows.map((s) => (
                                <tr key={s.id} style={{ borderTop: `1px solid ${C.borderLight}` }}>
                                    <td style={{ padding: '9px 16px' }}>
                                        <div style={{ fontWeight: 600, color: C.text }}>{s.name}</div>
                                        <div style={{ fontSize: T.caption, color: C.textMuted, fontFamily: F.mono }}>
                                            {s.id}
                                        </div>
                                    </td>
                                    <td style={{ padding: '9px 16px', color: s.asns ? C.text : C.textMuted }}>
                                        {s.asns.toLocaleString()}
                                    </td>
                                    <td
                                        style={{
                                            padding: '9px 16px',
                                            fontWeight: 700,
                                            color: complianceColor(s.compliance),
                                        }}
                                    >
                                        {s.compliance ? `${s.compliance.toFixed(1)}%` : '—'}
                                    </td>
                                    <td style={{ padding: '9px 16px', color: C.textSub }}>
                                        {s.valid ? `${s.valid.toFixed(1)}%` : '—'}
                                    </td>
                                    <td style={{ padding: '9px 16px', color: C.textSub }}>
                                        {s.dsOk ? `${s.dsOk.toFixed(1)}%` : '—'}
                                    </td>
                                    <td style={{ padding: '9px 16px', color: C.textMuted }}>{s.last}</td>
                                    <td style={{ padding: '9px 16px' }}>
                                        <Pill
                                            label={s.status}
                                            color={s.status === 'Active' ? 'green' : 'amber'}
                                            dot={s.status === 'Active'}
                                        />
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                <Pager
                    total={filtered.length}
                    page={safePage}
                    pageCount={pageCount}
                    onPrev={() => setPage((p) => Math.max(0, p - 1))}
                    onNext={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                />
            </Card>
        </>
    );
}

// ── Purchase Orders Review ────────────────────────────────────────────────────
function PurchaseOrdersView({ rows }: { rows: InsightsPurchaseOrder[] }) {
    const [q, setQ] = useState('');
    const [page, setPage] = useState(0);
    const query = q.trim().toLowerCase();
    const filtered = rows.filter(
        (p) =>
            query === '' ||
            p.po.toLowerCase().includes(query) ||
            p.supplier.toLowerCase().includes(query),
    );
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const pageRows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
    const headers = ['PO', 'Supplier', 'Lines', 'Need-by', 'Status'];

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
                <input
                    value={q}
                    onChange={(e) => {
                        setQ(e.target.value);
                        setPage(0);
                    }}
                    placeholder="Search PO or supplier…"
                    style={{
                        width: 280,
                        maxWidth: '100%',
                        height: 38,
                        border: `1px solid ${C.border}`,
                        borderRadius: 6,
                        padding: '0 12px',
                        fontSize: T.body,
                        fontFamily: F.ui,
                        boxSizing: 'border-box',
                    }}
                />
            </div>
            <Card>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.body }}>
                    <thead>
                        <tr style={{ background: C.bg, textAlign: 'left', color: C.textSub }}>
                            {headers.map((h) => (
                                <th key={h} style={{ padding: '9px 16px', fontWeight: 600 }}>
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.length === 0 ? (
                            <EmptyRow span={headers.length} message="No purchase orders to show yet." />
                        ) : (
                            pageRows.map((p) => (
                                <tr key={p.po} style={{ borderTop: `1px solid ${C.borderLight}` }}>
                                    <td style={{ padding: '9px 16px', fontFamily: F.mono, color: C.primary }}>
                                        {p.po}
                                    </td>
                                    <td style={{ padding: '9px 16px' }}>{p.supplier}</td>
                                    <td style={{ padding: '9px 16px' }}>{p.lines}</td>
                                    <td style={{ padding: '9px 16px', color: C.textMuted }}>{p.needBy}</td>
                                    <td style={{ padding: '9px 16px' }}>{p.status}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                <Pager
                    total={filtered.length}
                    page={safePage}
                    pageCount={pageCount}
                    onPrev={() => setPage((p) => Math.max(0, p - 1))}
                    onNext={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                />
            </Card>
        </>
    );
}

// ── PO Lines Review ───────────────────────────────────────────────────────────
function PoLinesView({ rows }: { rows: InsightsPoLine[] }) {
    const [q, setQ] = useState('');
    const [page, setPage] = useState(0);
    const query = q.trim().toLowerCase();
    const filtered = rows.filter(
        (l) =>
            query === '' ||
            l.po.toLowerCase().includes(query) ||
            l.item.toLowerCase().includes(query) ||
            l.supplier.toLowerCase().includes(query),
    );
    const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const safePage = Math.min(page, pageCount - 1);
    const pageRows = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
    const headers = ['PO', 'Line', 'Item', 'Supplier', 'Ordered', 'Received', 'Need-by'];

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 14 }}>
                <input
                    value={q}
                    onChange={(e) => {
                        setQ(e.target.value);
                        setPage(0);
                    }}
                    placeholder="Search PO, item, supplier…"
                    style={{
                        width: 280,
                        maxWidth: '100%',
                        height: 38,
                        border: `1px solid ${C.border}`,
                        borderRadius: 6,
                        padding: '0 12px',
                        fontSize: T.body,
                        fontFamily: F.ui,
                        boxSizing: 'border-box',
                    }}
                />
            </div>
            <Card>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: T.body }}>
                    <thead>
                        <tr style={{ background: C.bg, textAlign: 'left', color: C.textSub }}>
                            {headers.map((h) => (
                                <th key={h} style={{ padding: '9px 16px', fontWeight: 600 }}>
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pageRows.length === 0 ? (
                            <EmptyRow span={headers.length} message="No PO lines to show yet." />
                        ) : (
                            pageRows.map((l) => (
                                <tr key={`${l.po}-${l.line}`} style={{ borderTop: `1px solid ${C.borderLight}` }}>
                                    <td style={{ padding: '9px 16px', fontFamily: F.mono, color: C.primary }}>
                                        {l.po}
                                    </td>
                                    <td style={{ padding: '9px 16px' }}>{l.line}</td>
                                    <td style={{ padding: '9px 16px', fontFamily: F.mono }}>{l.item}</td>
                                    <td style={{ padding: '9px 16px' }}>{l.supplier}</td>
                                    <td style={{ padding: '9px 16px' }}>{l.ordered}</td>
                                    <td style={{ padding: '9px 16px' }}>{l.received}</td>
                                    <td style={{ padding: '9px 16px', color: C.textMuted }}>{l.needBy}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
                <Pager
                    total={filtered.length}
                    page={safePage}
                    pageCount={pageCount}
                    onPrev={() => setPage((p) => Math.max(0, p - 1))}
                    onNext={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
                />
            </Card>
        </>
    );
}

// ── Main Insights area ────────────────────────────────────────────────────────
const EMPTY_OVERVIEW: InsightsOverview = {
    supplierCount: 0,
    asnCount: 0,
    onTimePct: 0,
    errorRate: 0,
};

const TITLES: Record<InsightsView, { title: string; subtitle: string }> = {
    dashboard: { title: 'Overview', subtitle: 'Aggregate ASN metrics across suppliers' },
    transactions: { title: 'All ASNs', subtitle: 'Every ASN received across all suppliers' },
    suppliers: { title: 'Suppliers', subtitle: 'Supplier performance and compliance' },
    pos: { title: 'Purchase Orders', subtitle: 'PO coverage and status' },
    polines: { title: 'PO Lines', subtitle: 'Line-level coverage against need-by dates' },
};

export default function SupplierInsightsScreen() {
    const [view, setView] = useState<InsightsView>('dashboard');
    const [overview, setOverview] = useState<InsightsOverview>(EMPTY_OVERVIEW);
    const [suppliers, setSuppliers] = useState<InsightsSupplier[]>([]);
    const [transactions, setTransactions] = useState<InsightsTransaction[]>([]);
    const [pos, setPos] = useState<InsightsPurchaseOrder[]>([]);
    const [poLines, setPoLines] = useState<InsightsPoLine[]>([]);

    useEffect(() => {
        let live = true;
        const svc = getSupplierInsightsService();
        Promise.all([
            svc.getOverview(),
            svc.listSuppliers(),
            svc.listTransactions(),
            svc.listPurchaseOrders(),
            svc.listPoLines(),
        ])
            .then(([o, s, t, p, l]) => {
                if (!live) return;
                setOverview(o);
                setSuppliers(s);
                setTransactions(t);
                setPos(p);
                setPoLines(l);
            })
            .catch(() => {
                if (!live) return;
                setOverview(EMPTY_OVERVIEW);
                setSuppliers([]);
                setTransactions([]);
                setPos([]);
                setPoLines([]);
            });
        return () => {
            live = false;
        };
    }, []);

    return (
        <Shell
            navItems={INSIGHTS_NAV}
            active={view}
            accent={C.blue}
            onNav={(id) => setView(id as InsightsView)}
        >
            <PageTitle
                title={TITLES[view].title}
                subtitle={TITLES[view].subtitle}
                right={<Pill label="Internal" color="blue" dot />}
            />

            {view === 'dashboard' ? <OverviewView overview={overview} /> : null}
            {view === 'transactions' ? <TransactionsView rows={transactions} /> : null}
            {view === 'suppliers' ? <SuppliersView rows={suppliers} /> : null}
            {view === 'pos' ? <PurchaseOrdersView rows={pos} /> : null}
            {view === 'polines' ? <PoLinesView rows={poLines} /> : null}
        </Shell>
    );
}
